import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem('token'); } catch { return null; }
  });
  const [username, setUsername] = useState(() => {
    try { return sessionStorage.getItem('username'); } catch { return null; }
  });

  useEffect(() => {
    try {
      if (token) sessionStorage.setItem('token', token);
      else sessionStorage.removeItem('token');
      if (username) sessionStorage.setItem('username', username);
      else sessionStorage.removeItem('username');
    } catch {}
  }, [token, username]);

  const login = async (u, p) => {
    const r = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: u, password: p })
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      throw new Error(e.error || 'Erreur');
    }
    const data = await r.json();
    setToken(data.token);
    setUsername(data.username);
  };

  const logout = () => { setToken(null); setUsername(null); };

  const authFetch = async (url, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!(options.body instanceof FormData) && options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    const r = await fetch(url, { ...options, headers });
    if (r.status === 401) { logout(); throw new Error('Non authentifié'); }
    return r;
  };

  // Envoie une image et renvoie son URL.
  // En cas d'échec, lève une erreur avec un message lisible (image trop lourde, mauvais format...).
  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('image', file);
    const r = await authFetch('/api/upload', { method: 'POST', body: fd });
    if (!r.ok) throw new Error(await errorMessage(r));
    const d = await r.json();
    return d.url;
  };

  // Envoie un document PDF/Word dans S3 et renvoie { url, name, size, type }
  const uploadDocument = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await authFetch('/api/admin/files', { method: 'POST', body: fd });
    if (!r.ok) throw new Error(await errorMessage(r));
    return r.json();
  };

  // Envoie une courte vidéo (cartes de révision) avec suivi de la progression ; renvoie { url, name, size }
  const uploadVideo = (file, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/admin/videos');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status === 401) { logout(); return reject(new Error('Non authentifié')); }
      if (xhr.status === 413) return reject(new Error('Vidéo trop lourde pour le serveur (limite Nginx client_max_body_size)'));
      if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(data.error || `Erreur ${xhr.status}`));
      resolve(data);
    };
    xhr.onerror = () => reject(new Error('Connexion interrompue pendant l\'envoi'));
    const fd = new FormData();
    fd.append('file', file);
    xhr.send(fd);
  });

  return (
    <AuthContext.Provider value={{ token, username, login, logout, authFetch, uploadImage, uploadDocument, uploadVideo }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Extrait le message d'erreur d'une réponse de l'API, même si elle n'est pas en JSON
// (ex : page d'erreur Nginx quand le fichier dépasse sa limite).
export async function errorMessage(r) {
  if (r.status === 413) return 'Fichier ou contenu trop volumineux';
  try {
    const d = await r.json();
    if (d && d.error) return d.error;
  } catch {}
  return `Erreur ${r.status}`;
}
